import React, { useEffect, useState } from 'react';
import { colors } from './styles/colors';
import Button from './components/Button';
import AppleIcon from './components/AppleIcon';
import './styles/global.css';
import appScreenshot from './assets/app-screenshot.png';
import logo from './assets/logo.png';

// Updated items with the provided content
const topRowItems = [
  "🍽️ Find 3 lunch spots under $10",
  "💰 Save money by skipping delivery apps",
  "🍔 Compare burger deals across Waterloo",
  "🧋 Get bubble tea without the markup",
  "⏰ Know when to eat where",
  "🎓 Hit all the top student spots",
  "🍹 Never miss happy hour again",
  "💸 Spend less without sacrificing flavour",
  "🔥 Try the hottest promo of the week"
];

const bottomRowItems = [
  "🍽️ Eat out 3 times this week for under $30",
  "📊 Make smarter food choices with data",
  "📱 Stop walking past posters you never notice",
  "🎁 Unlock first access to exclusive deals",
  "🗺️ Explore new restaurants off your radar",
  "📋 Build your own go-to deal list",
  "💯 Never pay full price",
  "⏱️ Plan meals around peak promo hours"
];

// Debug component to help diagnose scrolling issues
interface ScrollDebuggerProps {
  visible: boolean;
}

const ScrollDebugger = ({ visible }: ScrollDebuggerProps) => {
  const [scrollData, setScrollData] = useState({
    scrollHeight: 0,
    clientHeight: 0,
    windowHeight: 0,
    documentHeight: 0
  });

  useEffect(() => {
    const updateScrollData = () => {
      setScrollData({
        scrollHeight: document.body.scrollHeight,
        clientHeight: document.documentElement.clientHeight,
        windowHeight: window.innerHeight,
        documentHeight: Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        )
      });
    };

    if (visible) {
      updateScrollData();
      window.addEventListener('scroll', updateScrollData);
      window.addEventListener('resize', updateScrollData);
    }

    return () => {
      window.removeEventListener('scroll', updateScrollData);
      window.removeEventListener('resize', updateScrollData);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      right: 20,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '10px 15px',
      borderRadius: 8,
      fontSize: 12,
      fontFamily: 'monospace',
      zIndex: 9999,
      maxWidth: 300,
    }}>
      <h4 style={{ margin: '0 0 10px' }}>Scroll Debugger</h4>
      <div>Body scrollHeight: {scrollData.scrollHeight}px</div>
      <div>Document clientHeight: {scrollData.clientHeight}px</div>
      <div>Window innerHeight: {scrollData.windowHeight}px</div>
      <div>Document height: {scrollData.documentHeight}px</div>
      <div style={{ fontSize: 10, marginTop: 10, opacity: 0.7 }}>
        Press Shift+D to toggle
      </div>
    </div>
  );
};

function App() {
  // Duplicate arrays for seamless scrolling
  const duplicatedTopRow = [...topRowItems, ...topRowItems, ...topRowItems];
  const duplicatedBottomRow = [...bottomRowItems, ...bottomRowItems, ...bottomRowItems];
  const [showDebugger, setShowDebugger] = useState(false);

  // Force layout recalculation when content changes or on window resize
  useEffect(() => {
    const handleResize = () => {
      // Force layout recalculation
      document.body.style.minHeight = "0";
      setTimeout(() => {
        document.body.style.minHeight = "";
      }, 0);
    };

    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle debugger with Shift+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'D') {
        setShowDebugger(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div style={{ 
      width: '100%', 
      minHeight: '100%', 
      backgroundColor: colors.primaryPurple,
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'visible'
    }}>
      <ScrollDebugger visible={showDebugger} />
      
      <header className="mobile-header" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: colors.white,
        backgroundColor: 'rgba(93, 63, 211, 0.8)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 1000,
        transition: 'background-color 0.3s ease'
      }}>
        <img src={logo} alt="Savor Logo" style={{ height: '36px', width: 'auto', borderRadius: '9999px' }} />
      </header>

      <main className="mobile-main" style={{
        position: 'relative',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 48px',
        paddingTop: '160px',
        paddingBottom: '60px',
        color: colors.white,
        textAlign: 'center'
      }}>
        <h2 style={{
          fontSize: '64px',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          marginBottom: '24px',
          lineHeight: 1.1
        }}>
          Find better food deals,
          <br />
          right around you.
        </h2>
        <p className="intro-paragraph" style={{
          fontSize: '24px',
          fontWeight: 400,
          color: 'rgba(255, 255, 255, 0.9)',
          maxWidth: '720px',
          margin: '0 auto',
          lineHeight: 1.4,
          marginBottom: '40px'
        }}>
          The only way for UW students to find all local food deals in one place –– one app, one map.
        </p>
        <div className="first-cta-button-wrapper" style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Button
            icon={<AppleIcon />}
            onClick={() => window.open('https://apps.apple.com', '_blank')}
            style={{ borderRadius: '9999px' }}
          >
            Get the App
          </Button>
        </div>

        {/* Main placeholder image */}
        <div 
          className="app-screenshot-container" 
          style={{
            backgroundImage: `url(${appScreenshot})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            margin: '0 auto'
          }}
        />

        {/* Text section */}
        <div className="empty-spacer-div" style={{
          maxWidth: '720px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
        </div>

        {/* Final CTA section */}
        <div style={{
          maxWidth: '720px',
          margin: '0 auto',
          textAlign: 'center',
          marginBottom: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontSize: '64px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            marginBottom: '24px',
            lineHeight: 1.1
          }}>
            Start Savor(ing).
          </h2>
          <p style={{
            fontSize: '24px',
            fontWeight: 400,
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
            marginBottom: '32px'
          }}>
            Savor the Deals that are available to you.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '30px'
          }}>
            <Button
              icon={<AppleIcon />}
              onClick={() => window.open('https://apps.apple.com', '_blank')}
              style={{ borderRadius: '9999px' }}
            >
              Get the App
            </Button>
          </div>
        </div>

        {/* Scrolling content - moved here */}
        <div style={{
          width: '100%',
          overflow: 'hidden',
          marginBottom: '80px'
        }}>
          {/* Top row - scrolling left */}
          <div className="scroll-container" style={{
            marginBottom: '20px',
          }}>
            <div className="scroll-left mobile-scroll-content">
              {duplicatedTopRow.map((item, index) => (
                <div
                  key={index}
                  className="mobile-scroll-item"
                  style={{
                    padding: '16px 24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '100px',
                    color: colors.white,
                    fontSize: '18px',
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom row - scrolling right */}
          <div className="scroll-container">
            <div className="scroll-right mobile-scroll-content">
              {duplicatedBottomRow.map((item, index) => (
                <div
                  key={index}
                  className="mobile-scroll-item"
                  style={{
                    padding: '16px 24px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '100px',
                    color: colors.white,
                    fontSize: '18px',
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <footer className="mobile-footer" style={{
          maxWidth: '720px',
          margin: '0 auto',
          textAlign: 'center',
          color: colors.white,
          paddingBottom: '60px'
        }}>
          <div style={{
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '28px',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              marginBottom: '12px'
            }}>
              Savor
            </h3>
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '12px'
            }}>
              An iOS app made by UW Friends
            </p>
            <a 
              href="mailto:contact@savor.app" 
              style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colors.white;
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Contact us
            </a>
          </div>

          <div style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.6)',
          }}>
            © 2025 Savor. All rights reserved.
          </div>
        </footer>
      </main>
    </div>
  );
}

export default App;
